import React from 'react';
import { DocsLayout } from '../components/DocsLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Cookie, Info } from 'lucide-react';

export const CookiePolicyPage: React.FC = () => {
  return (
    <DocsLayout
      title='Cookie Policy'
      description='How Vehix uses cookies and similar technologies'
      breadcrumbs={[
        { label: 'Documentation', path: '/docs' },
        { label: 'Legal', path: '/docs/legal' },
        { label: 'Cookie Policy' }
      ]}
    >
      <div className='max-w-[1000px]'>
        <p className='text-sm text-muted-foreground italic mb-8'>Last Updated: January 28, 2026</p>

        <div className='mb-8'>
          <Card className='border-blue-500/50 bg-blue-500/5'>
            <CardHeader>
              <div className='flex items-center gap-2'>
                <Cookie className='h-5 w-5 text-blue-600' />
                <CardTitle className='text-lg text-blue-700 dark:text-blue-400'>
                  What Are Cookies?
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className='text-sm leading-6'>
              <p>
                Cookies are small files stored on your device when you use a website. They help
                websites remember things about you, like your preferences and whether you are logged
                in. Almost every website uses them.
              </p>
            </CardContent>
          </Card>
        </div>

        <section className='mb-20' id='why-we-use-cookies'>
          <h2 className='text-3xl font-bold mb-6'>1. Why We Use Cookies</h2>
          <p className='text-sm leading-6 mb-4'>
            We use cookies to make Vehix work properly and to give you a better experience:
          </p>
          <ul className='list-disc pl-6 space-y-2'>
            <li className='text-sm leading-6'>
              <strong>Keep you logged in:</strong> So you do not have to enter your password every
              time
            </li>
            <li className='text-sm leading-6'>
              <strong>Remember your preferences:</strong> Like dark mode or your preferred view
              settings
            </li>
            <li className='text-sm leading-6'>
              <strong>Keep you secure:</strong> Help protect your account from unauthorized access
            </li>
            <li className='text-sm leading-6'>
              <strong>Make things faster:</strong> Store temporary information so pages load quicker
            </li>
          </ul>
        </section>

        <section className='mb-20' id='types-of-cookies'>
          <h2 className='text-3xl font-bold mb-6'>2. Types of Cookies We Use</h2>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>2.1 Essential Cookies (Required)</h3>
            <p className='text-sm leading-6 mb-4'>
              These cookies are necessary for Vehix to work. Without them, you could not log in or
              use the service.
            </p>
            <div className='bg-muted/30 rounded-lg p-4 border border-border'>
              <ul className='list-disc pl-6 space-y-2'>
                <li className='text-sm leading-6'>
                  <strong>Authentication cookies:</strong> Keep you logged in while you use Vehix
                </li>
                <li className='text-sm leading-6'>
                  <strong>Security cookies:</strong> Help protect your account
                </li>
                <li className='text-sm leading-6'>
                  <strong>Session cookies:</strong> Remember your current session
                </li>
              </ul>
            </div>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>2.2 Preference Cookies (Helpful)</h3>
            <p className='text-sm leading-6 mb-4'>
              These cookies remember your choices to make Vehix more convenient:
            </p>
            <div className='bg-muted/30 rounded-lg p-4 border border-border'>
              <ul className='list-disc pl-6 space-y-2'>
                <li className='text-sm leading-6'>
                  <strong>Theme preference:</strong> Remember if you prefer dark or light mode
                </li>
                <li className='text-sm leading-6'>
                  <strong>View settings:</strong> Remember how you like to see information
                </li>
                <li className='text-sm leading-6'>
                  <strong>Language:</strong> Remember your language preference
                </li>
              </ul>
            </div>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>2.3 Performance Cookies (Optional)</h3>
            <p className='text-sm leading-6 mb-4'>
              These cookies help us understand how Vehix is being used so we can make it better:
            </p>
            <div className='bg-muted/30 rounded-lg p-4 border border-border'>
              <ul className='list-disc pl-6 space-y-2'>
                <li className='text-sm leading-6'>
                  <strong>Usage patterns:</strong> Which features are used most often
                </li>
                <li className='text-sm leading-6'>
                  <strong>Error tracking:</strong> When something goes wrong so we can fix it
                </li>
                <li className='text-sm leading-6'>
                  <strong>Performance data:</strong> How fast pages load
                </li>
              </ul>
            </div>
          </div>
        </section>

        <section className='mb-20' id='what-cookies-do-not-do'>
          <h2 className='text-3xl font-bold mb-6'>3. What Our Cookies Do Not Do</h2>
          <p className='text-sm leading-6 mb-4'>Our cookies do NOT:</p>
          <ul className='list-disc pl-6 space-y-2'>
            <li className='text-sm leading-6'>Track you across other websites</li>
            <li className='text-sm leading-6'>
              Collect personal information beyond what is needed
            </li>
            <li className='text-sm leading-6'>Share your data with advertisers</li>
            <li className='text-sm leading-6'>Show you targeted ads</li>
            <li className='text-sm leading-6'>Sell any information about you</li>
          </ul>
        </section>

        <section className='mb-20' id='third-party-cookies'>
          <h2 className='text-3xl font-bold mb-6'>4. Third-Party Cookies</h2>
          <p className='text-sm leading-6 mb-4'>
            Some of the services we use may also set cookies:
          </p>
          <ul className='list-disc pl-6 space-y-2'>
            <li className='text-sm leading-6'>
              <strong>Google:</strong> For secure sign-in (if you use Google to log in)
            </li>
            <li className='text-sm leading-6'>
              <strong>Supabase:</strong> For authentication and session management
            </li>
          </ul>
          <p className='text-sm leading-6 mt-4'>
            These services have their own cookie policies. We do not control their cookies, but we
            only work with trusted providers.
          </p>
        </section>

        <section className='mb-20' id='managing-cookies'>
          <h2 className='text-3xl font-bold mb-6'>5. Managing Your Cookies</h2>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>5.1 Browser Settings</h3>
            <p className='text-sm leading-6 mb-4'>
              You can control cookies through your web browser settings. Most browsers let you:
            </p>
            <ul className='list-disc pl-6 space-y-2'>
              <li className='text-sm leading-6'>See what cookies are stored on your device</li>
              <li className='text-sm leading-6'>Delete some or all cookies</li>
              <li className='text-sm leading-6'>Block cookies from certain websites</li>
              <li className='text-sm leading-6'>Get notified when a cookie is set</li>
            </ul>
          </div>

          <div className='mb-6'>
            <Card className='border-amber-500/50 bg-amber-500/5'>
              <CardHeader>
                <div className='flex items-center gap-2'>
                  <Info className='h-5 w-5 text-amber-600' />
                  <CardTitle className='text-lg text-amber-700 dark:text-amber-400'>
                    Important Note
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className='text-sm leading-6'>
                <p>
                  If you block essential cookies, Vehix may not work properly. You might not be able
                  to log in or use certain features. We recommend keeping essential cookies enabled.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>5.2 How to Manage Cookies</h3>
            <p className='text-sm leading-6 mb-4'>
              Here is where to find cookie settings in common browsers:
            </p>
            <ul className='list-disc pl-6 space-y-2'>
              <li className='text-sm leading-6'>
                <strong>Chrome:</strong> Settings &gt; Privacy and security &gt; Cookies
              </li>
              <li className='text-sm leading-6'>
                <strong>Firefox:</strong> Settings &gt; Privacy & Security &gt; Cookies
              </li>
              <li className='text-sm leading-6'>
                <strong>Safari:</strong> Preferences &gt; Privacy &gt; Cookies
              </li>
              <li className='text-sm leading-6'>
                <strong>Edge:</strong> Settings &gt; Cookies and site permissions
              </li>
            </ul>
          </div>
        </section>

        <section className='mb-20' id='cookie-duration'>
          <h2 className='text-3xl font-bold mb-6'>6. How Long Cookies Last</h2>
          <ul className='list-disc pl-6 space-y-2'>
            <li className='text-sm leading-6'>
              <strong>Session cookies:</strong> Deleted when you close your browser
            </li>
            <li className='text-sm leading-6'>
              <strong>Preference cookies:</strong> Typically last 1 year (so we remember your
              settings)
            </li>
            <li className='text-sm leading-6'>
              <strong>Authentication cookies:</strong> Last until you log out or they expire for
              security
            </li>
          </ul>
        </section>

        <section className='mb-20' id='updates'>
          <h2 className='text-3xl font-bold mb-6'>7. Updates to This Policy</h2>
          <p className='text-sm leading-6'>
            We may update this Cookie Policy from time to time. If we make significant changes, we
            will let you know by updating the date at the top of this page.
          </p>
        </section>

        <section className='mb-20' id='contact'>
          <h2 className='text-3xl font-bold mb-6'>8. Questions?</h2>
          <p className='text-sm leading-6 mb-6'>
            If you have questions about our use of cookies, contact us:
          </p>

          <div className='p-6 bg-muted/30 rounded-lg border border-border'>
            <p className='text-sm leading-6'>
              <strong>Kebi AI LLC</strong>
              <br />
              Email: <span className='text-primary'>privacy@kebi.ai</span>
              <br />
              Support: <span className='text-primary'>support@kebi.ai</span>
            </p>
          </div>
        </section>
      </div>
    </DocsLayout>
  );
};
